import React, { useCallback, useEffect, useState } from 'react';
import { makeStyles, Button, Select, IconButton, MenuItem, Checkbox, ListItemText, Grid } from '@material-ui/core';
import { injectIntl } from 'react-intl';
import * as API from 'services/api';
import { useSelector, useDispatch } from 'react-redux';
import { setLoading } from 'redux/actions/app';
import { get10LastLetters } from 'utils/validations'
import { CustomTable, RemoveModal } from 'components'
import OrderDetails from './detail';
import EmptyPage from 'components/EmptyPage';
import { appProfileSelector } from 'redux/selectors';
import { statues } from 'config';
import IconSave from "@material-ui/icons/Save";
import IconDelete from "@material-ui/icons/Delete";
import PropTypes from 'prop-types';
import { tableOptions } from 'pages/common';


const useStyles = makeStyles(theme => ({
  placeholder: {
    paddingLeft: theme.spacing(3)
  },
  table: {
    backgroundColor: 'white'
  },
  cartBtn: {
    color: "#05A969"
  },
}));

const Orders = ({
  intl: { formatMessage }
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [orders, setOrders] = useState([])
  const [openModal, setOpenModal] = useState(false);
  const profile = useSelector(appProfileSelector);
  const [openDelete, setOpenDelete] = useState();

  useEffect(() => {
    onLoad();
  }, []);
  const onLoad = () => {
    dispatch(setLoading(true));
    API.listUsers({})
      .then(res => {
        dispatch(setLoading(false));
        if (res.data && res.data.length > 0) {
          setOrders(res.data.filter(item => item.role == "user"))
        }
      })
      .catch(() => {
        dispatch(setLoading(false));
      });
  }
  const onSave = (user) => e => {
    dispatch(setLoading(true));
    API.updateUser({
      id: user.id,
      permission: user.permission,
      status: user.status
    })
      .then(res => {
        dispatch(setLoading(false));
        if (res.code != 200) {
          onLoad();
        }
      })
      .catch(() => {
        dispatch(setLoading(false));
        onLoad();
      });
  }
  const onDelete = (user) => e => {
    setOpenDelete(user)
  }
  const deleteUser = (user) => {
    dispatch(setLoading(true));
    API.deleteUser({
      id: user.id
    })
      .then(res => {
        dispatch(setLoading(false));
        if (res.code == 200) {
          setOpenDelete();
          onLoad();
        }
      })
      .catch(() => {
        dispatch(setLoading(false));
      });
  }

  let companies = [];
  if (profile.siret) {
    companies.push({ name: profile.company, siret: profile.siret, email: profile.clientEmail || profile.email })
  }
  if (profile.establishments && profile.establishments.length > 0) {
    companies.push(...profile.establishments)
  }

  const tableColumns = [
    {
      name: 'id',
      label: "ID",
      options: {
        display: true,
        sort: false,
        customBodyRender: (value) => get10LastLetters(value)
      }
    },
    {
      name: 'firstName',
      label: formatMessage({ id: "first_name" }),
      options: {
        display: true,
        sort: true
      }
    },
    {
      name: 'lastName',
      label: formatMessage({ id: "last_name" }),
      options: {
        display: true,
        sort: true
      }
    },
    {
      name: 'email',
      label: formatMessage({ id: "email" }),
      options: {
        display: true,
        sort: true
      }
    },
    ...companies.length > 0 ? [
      {
        name: 'sirets',
        label: "Etablissement secondaire",
        options: {
          display: true,
          sort: false,
          customBodyRender: (value, tableMeta) => renderSirets(tableMeta, orders, companies)
        }
      },
    ] : [],
    {
      name: 'domain',
      label: "Droits et visibilité",
      options: {
        display: true,
        sort: false,
        customBodyRender: useCallback((value, tableMeta) => {
          const id = tableMeta.rowData[0];
          const order = orders.find(item => item.id == id);
          let visibilities = [];
          if (!order.permission?.homepage?.hasOwnProperty("enable")) {
            if (!profile.clientPermission?.homepage?.hasOwnProperty("enable")) {
              if (profile.siret) {
                visibilities.push("formation");
              }
            } else if (profile?.clientPermission?.formation?.enable) {
              visibilities.push("formation");
            }
          } else {
            if (order?.permission?.inspection?.enable) {
              visibilities.push("inspection")
            }
            if (order?.permission?.formation?.enable) {
              visibilities.push("formation")
            }
          }
          return <Select
            multiple
            value={visibilities}
            onChange={e => {
              const newPermission = {
                ...order.permission,
                homepage: { enable: true },
                inspection: {
                  ...order.permission?.inspection,
                  enable: e.target.value.includes("inspection")
                },
                formation: {
                  ...order.permission?.inspection,
                  enable: e.target.value.includes("formation")
                },
              }
              setOrders(orders.map(el => el.id == tableMeta.rowData[0] ? { ...el, permission: newPermission } : el))
            }}
            renderValue={(selected) => selected.map(el => el.toUpperCase()).join(', ')}
            variant="outlined"
            style={{ minWidth: 100 }}
          >
            {
              profile.compteSEL &&
              <MenuItem value={"inspection"}>
                <Checkbox checked={visibilities.indexOf("inspection") > -1} />
                <ListItemText primary={"Inspection"} />
              </MenuItem>
            }
            <MenuItem value={"formation"}>
              <Checkbox checked={visibilities.indexOf("formation") > -1} />
              <ListItemText primary={"Formation"} />
            </MenuItem>
          </Select>
        }, [orders])
      }
    },
    {
      name: 'status',
      label: formatMessage({ id: "status" }),
      options: {
        display: true,
        sort: false,
        customBodyRender: useCallback((value, tableMeta) => {
          return <Select
            value={value}
            onChange={e => {
              setOrders(orders.map(el => el.id == tableMeta.rowData[0] ? { ...el, status: e.target.value } : el))
            }}
            variant="outlined"
            style={{ minWidth: 100 }}
          >
            {
              statues.map(item => <MenuItem key={item} value={item}>
                {formatMessage({ id: "user.status." + item })}
              </MenuItem>)
            }
          </Select>
        }, [orders])
      }
    },
    {
      name: 'actions',
      label: "ACTIONS",
      options: {
        display: true,
        sort: false,
        customBodyRender: useCallback((value, tableMeta) => {
          const id = tableMeta.rowData[0];
          const order = orders.find(item => item.id == id)
          return <Grid container>
            <IconButton size="small" className={classes.cartBtn}
              onClick={onSave(order)}
            ><IconSave /></IconButton>
            &nbsp;
            &nbsp;
            <IconButton size="small" className={classes.cartBtn} onClick={onDelete(order)}><IconDelete /></IconButton>
          </Grid>
        }, [orders])
      }
    }
  ];
  const renderSirets = useCallback((tableMeta, orders, companies) => {
    const id = tableMeta.rowData[0];
    const order = orders.find(item => item.id == id);
    const sirets = order?.permission?.sirets;
    return <Select
      multiple
      value={sirets}
      onChange={e => {
        const newPermission = {
          ...order.permission,
          sirets: e.target.value
        }
        setOrders(orders.map(el => el.id == tableMeta.rowData[0] ? { ...el, permission: newPermission } : el))
      }}
      renderValue={renderValue}
      variant="outlined"
      style={{ minWidth: 150 }}
    >
      {companies.map((item) => (
        <MenuItem key={item._id} value={item.siret}>
          <Checkbox checked={sirets.indexOf(item.siret) > -1} />
          <ListItemText primary={item.name} />
        </MenuItem>
      ))}
    </Select>
  }, [])
  const renderValue = (selected) => selected.map(el => companies.find(item => item.siret == el).name).join(', ');

  return (
    <div>
      {
        orders.length > 0 ?
          <>
            <Grid container justify='flex-end' style={{ marginBottom: 4 }}>
              <Button size='small' variant='contained' color="primary" className={classes.button} style={{ backgroundColor: '#05A969', borderRadius: 100 }}
                onClick={() => {
                  setOpenModal(true)
                }}
              >Inviter un utilisateur</Button>
            </Grid>
            <CustomTable
              columns={tableColumns}
              data={orders}
              options={{
                ...tableOptions(),
              }}
            />
          </> :
          <EmptyPage title="Vous êtes le seul utilisateur à ce jour, invitez vos collègues !" buttonLabel="Inviter un utilisateur"
            onClick={() => {
              setOpenModal(true)
            }}
          />
      }
      {
        openModal &&
        <OrderDetails
          open={openModal}
          onClose={(refresh) => {
            if (refresh) onLoad();
            setOpenModal(false)
          }}
        />
      }
      {openDelete && (
        <RemoveModal
          visible={!!openDelete}
          onClose={() => setOpenDelete()}
          onConfirm={() => {
            deleteUser(openDelete)
          }}
          loading={false}
          title="Suppression d’un utilisateur"
          description={"Souhaitez-vous supprimer cet utilisateur ?"}
        />
      )}
    </div>
  );
};
Orders.propTypes = {
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired
  })
};
export default injectIntl(Orders);
